import truncate from 'lodash/truncate';

import config from 'data/config';
import moment from 'data/moment';
import { Uuid } from 'types/common';
import { CurrencyCode } from 'types/models';

type FormatTimeOptions = {
  showHours?: boolean;
  showMinutes?: boolean;
};

const formatCardCode = (value: string) => {
  return value.replace(/\D/g, '').substring(0, config.CARD_CODE_MAX_LENGTH);
};

const formatCardExpiry = (value: string) => {
  const numbers = value.replace(/\D/g, '').substring(0, 4);

  if (numbers.length >= 2) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}`;
  }

  return numbers;
};

const formatCardNumber = (value: string) => {
  const numbers = value.replace(/\D/g, '').substring(0, config.CARD_NUMBER_MAX_LENGTH);
  const parts = [];

  for (let i = 0, k = 4, n = numbers.length; i < n; i += k) {
    parts.push(numbers.substring(i, i + k));
  }

  return parts.join(' ');
};

const formatCurrency = (amount: number, currency: CurrencyCode) => {
  return `${currency} ${formatNumber(amount)}`;
};

const formatDate = (date: string) => {
  return moment(date).format(config.DATE_FORMAT);
};

const formatDateTime = (date: string) => {
  return moment(date).format(config.DATE_TIME_FORMAT);
};

const formatId = (id: Uuid) => {
  return `#${id}`;
};

const formatIdTruncated = (id: Uuid, length = 4, omission = '…') => {
  return formatId(truncate(id, {
    length: length + omission.length,
    omission,
  }));
};

const formatNumber = (value: number, precision = 2) => {
  const formatter = new Intl.NumberFormat('en', {
    style: 'decimal',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return formatter.format(value);
};

const formatPercent = (value: number, precision = 2) => {
  const formatter = new Intl.NumberFormat('en', {
    style: 'percent',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return formatter.format(value / 100);
};

const formatTime = (seconds: number, options?: FormatTimeOptions) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const showHours = options?.showHours || hours > 0;
  const showMinutes = showHours || options?.showMinutes || minutes > 0;

  const parts = [];

  if (showHours) {
    parts.push(hours.toString().padStart(2, '0'));
  }

  if (showMinutes) {
    parts.push(minutes.toString().padStart(2, '0'));
  }

  parts.push(secs.toString().padStart(2, '0'));

  return parts.join(':');
};

const transformFromCents = (value: number, precision = 2) => {
  return value / Math.pow(10, precision);
};

const transformToCents = (value: number, precision = 2) => {
  return Math.round(value * Math.pow(10, precision));
};

const formatter = {
  formatCardCode,
  formatCardExpiry,
  formatCardNumber,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatId,
  formatIdTruncated,
  formatNumber,
  formatPercent,
  formatTime,
  transformFromCents,
  transformToCents,
};

export default formatter;
